import { useAuth } from '../../Context/AuthenticationContext';

export interface UserIdentity {
  email: string;
  name: string;
  initials: string;
  photoUrl?: string;
}

/** Initials from a display name, falling back to the email local-part. */
export function getInitials(name: string, email: string): string {
  const fromName = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => (part[0] || '').toUpperCase())
    .join('');
  if (fromName) return fromName;
  const [local = ''] = email.split('@');
  return local
    .split('.')
    .slice(0, 2)
    .map(p => (p[0] || '').toUpperCase())
    .join('');
}

export function getDisplayName(email: string): string {
  const [local = ''] = email.split('@');
  return local
    .split('.')
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

export function formatDisplayName(displayName: string | undefined): string {
  const name = displayName?.trim();
  if (!name) return '';
  const commaIndex = name.indexOf(',');
  if (commaIndex > -1) {
    const last = name.slice(0, commaIndex).trim();
    const rest = name.slice(commaIndex + 1).trim();
    if (last && rest) return `${rest} ${last}`;
  }
  return name;
}

export function useUserIdentity(): UserIdentity | null {
  const { user } = useAuth();
  const email = user?.email?.trim() ?? '';
  const name = formatDisplayName(user?.displayName) || (email ? getDisplayName(email) : '');
  if (!name && !email) return null;
  return {
    email,
    name: name || email,
    initials: getInitials(name, email),
    photoUrl: user.photoUrl,
  };
}
